import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import api from "./services/api";
import { useAuth } from "./context/AuthContext";
import * as XLSX from "xlsx";

const POLL_MS = 10000;

const todayStr = () => new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD in the browser's timezone
const thisMonth = () => todayStr().slice(0, 7);

const shiftDate = (dateStr, days) => {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString("en-CA");
};

const prettyDate = (dateStr) =>
  new Date(dateStr + "T00:00:00").toLocaleDateString("en-GB", {
    weekday: "long", day: "numeric", month: "short", year: "numeric",
  });

const shortDate = (dateStr) =>
  new Date(dateStr + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });

const STATE_META = {
  in: { label: "Inside", cls: "bg-emerald-100 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  out: { label: "Left", cls: "bg-sky-100 text-sky-700 border-sky-200", dot: "bg-sky-500" },
  not_punched: { label: "Not punched", cls: "bg-rose-100 text-rose-700 border-rose-200", dot: "bg-rose-500" },
  weekly_off: { label: "Weekly off", cls: "bg-slate-100 text-slate-600 border-slate-200", dot: "bg-slate-400" },
  holiday: { label: "Holiday", cls: "bg-teal-100 text-teal-700 border-teal-200", dot: "bg-teal-500" },
  leave: { label: "On leave", cls: "bg-violet-100 text-violet-700 border-violet-200", dot: "bg-violet-500" },
};

const CELL_CLS = {
  P: "bg-emerald-100 text-emerald-800",
  "P/2": "bg-amber-100 text-amber-800",
  A: "bg-rose-100 text-rose-700",
  WO: "bg-slate-200 text-slate-600",
  L: "bg-violet-100 text-violet-700",
  H: "bg-teal-100 text-teal-700",
  MP: "bg-amber-100 text-amber-800",
  "": "bg-white text-slate-300",
};

const CAL_KIND_OPTIONS = [
  ["", "Normal day (weekly rule)"], ["holiday-paid", "Holiday - paid"],
  ["holiday-unpaid", "Holiday - unpaid"], ["working", "Working day (factory open on the weekly off)"],
];

const FIX_STATUS_OPTIONS = [
  ["", "Automatic (from punches)"], ["P", "P - Present"], ["P/2", "P/2 - Half day"],
  ["A", "A - Absent"], ["L", "L - Leave"], ["WO", "WO - Weekly off"],
];

const minutesAgo = (isoLikeUtc) => {
  if (!isoLikeUtc) return null;
  const t = new Date(isoLikeUtc.replace(" ", "T") + "Z").getTime();
  return isNaN(t) ? null : Math.max(0, Math.round((Date.now() - t) / 60000));
};

export default function LiveAttendance() {
  const { user } = useAuth() || {};
  const isAdmin = user && user.role === "admin";

  const [tab, setTab] = useState("live");
  const [toast, setToast] = useState(null);

  // ---- Live (one day) ----
  const [date, setDate] = useState(todayStr());
  const [live, setLive] = useState(null);
  const [liveError, setLiveError] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showHidden, setShowHidden] = useState(false);
  const reqSeq = useRef(0);

  const notify = (msg, type = "ok") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadLive = useCallback(async () => {
    const seq = ++reqSeq.current;
    try {
      const res = await api.get("/biometric/live", {
        params: { date, includeInactive: showHidden ? 1 : 0 },
      });
      if (seq !== reqSeq.current) return; // a newer request already answered
      setLive(res.data);
      setLiveError("");
      setUpdatedAt(new Date());
    } catch (err) {
      if (seq !== reqSeq.current) return;
      setLiveError(err.response?.data?.error || "Cannot reach the server. Retrying...");
    }
  }, [date, showHidden]);

  useEffect(() => {
    if (tab !== "live" || !isAdmin) return undefined;
    setLive(null);
    loadLive();
    if (date !== todayStr()) return undefined; // past days do not change
    const timer = setInterval(() => {
      if (!document.hidden) loadLive();
    }, POLL_MS);
    const onVisible = () => { if (!document.hidden) loadLive(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [tab, date, loadLive, isAdmin]);

  const visibleEmployees = useMemo(() => {
    if (!live) return [];
    const q = search.trim().toLowerCase();
    return live.employees.filter((e) => {
      if (filter === "punched" && e.state !== "in" && e.state !== "out") return false;
      if (filter !== "all" && filter !== "punched" && e.state !== filter) return false;
      if (q && !(e.name.toLowerCase().includes(q) || String(e.id) === q)) return false;
      return true;
    });
  }, [live, filter, search]);

  const toggleActive = async (emp) => {
    const hide = emp.active;
    const ok = window.confirm(
      hide
        ? `Hide ${emp.name} from attendance? (use this for someone who has left)`
        : `Show ${emp.name} in attendance again?`
    );
    if (!ok) return;
    try {
      await api.patch(`/biometric/employees/${emp.id}`, { active: !hide });
      notify(hide ? `${emp.name} hidden` : `${emp.name} is back on the list`);
      loadLive();
    } catch (err) {
      notify(err.response?.data?.error || "Could not update the employee", "error");
    }
  };

  // ---- Monthly ----
  const [ym, setYm] = useState(thisMonth());
  const [month, setMonth] = useState(null);
  const [monthLoading, setMonthLoading] = useState(false);
  const [monthError, setMonthError] = useState("");
  const [monthSearch, setMonthSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [editCell, setEditCell] = useState(null); // { emp, day, dateStr } of the day being corrected
  const [fixForm, setFixForm] = useState({ status: "", arrive: "", depart: "", ot: "", note: "" });
  const [savingFix, setSavingFix] = useState(false);

  // quiet: refresh in place (after a correction) without blanking the grid
  const loadMonth = useCallback(async (quiet = false) => {
    if (!quiet) setMonthLoading(true);
    setMonthError("");
    try {
      const res = await api.get(`/biometric/month/${ym}`);
      setMonth(res.data);
    } catch (err) {
      if (!quiet) setMonth(null);
      setMonthError(err.response?.data?.error || "Could not load the month");
    } finally {
      setMonthLoading(false);
    }
  }, [ym]);

  // emp needs { employeeId, name, empCode }; day is a day record from the month or attention list
  // In / Out start with the machine times, so only the missing or wrong one needs typing
  const openCell = (emp, day, dateStr) => {
    const f = day.edited || {};
    setFixForm({
      status: f.status || "", arrive: f.arrive || day.punchIn || "", depart: f.depart || day.punchOut || "",
      ot: f.ot || "", note: f.note || "",
    });
    setEditCell({ emp, day, dateStr });
  };

  // reset = remove the correction, so the day follows the punches again
  const saveFix = async (reset = false) => {
    if (!editCell) return;
    const { emp, day, dateStr } = editCell;
    // a machine time left as it is stays a punch, not a correction
    const body = reset ? {} : {
      ...fixForm,
      arrive: fixForm.arrive === (day.punchIn || "") ? "" : fixForm.arrive,
      depart: fixForm.depart === (day.punchOut || "") ? "" : fixForm.depart,
    };
    setSavingFix(true);
    try {
      await api.put(`/biometric/corrections/${emp.employeeId}/${dateStr}`, body);
      setEditCell(null);
      notify(reset ? `Day ${day.day} for ${emp.name} follows the punches again` : `Saved day ${day.day} for ${emp.name}`);
      if (month) loadMonth(true);
      loadAttention();
      if (tab === "live") loadLive();
    } catch (err) {
      notify(err.response?.data?.error || "Could not save the correction", "error");
    } finally {
      setSavingFix(false);
    }
  };

  // ---- Needs attention (punch problems on past days) ----
  const [attention, setAttention] = useState(null);
  const [attnDays, setAttnDays] = useState(7);

  const loadAttention = useCallback(async () => {
    try {
      const res = await api.get("/biometric/attention", { params: { days: attnDays } });
      setAttention(res.data);
    } catch (err) {
      setAttention(null);
    }
  }, [attnDays]);

  // loaded on every tab, so the count on the "Needs attention" tab is always current
  useEffect(() => {
    if (isAdmin) loadAttention();
  }, [tab, loadAttention, isAdmin]);

  // ---- Settings: staff names / dates and the WhatsApp summary ----
  const [staff, setStaff] = useState(null);
  const [staffEdits, setStaffEdits] = useState({}); // id -> changed fields not saved yet
  const [savingStaff, setSavingStaff] = useState(null);
  const [alertCfg, setAlertCfg] = useState(null); // { settings, lastSent, whatsapp }
  const [alertForm, setAlertForm] = useState(null);
  const [alertPreview, setAlertPreview] = useState(null); // { kind, text }
  const [alertResults, setAlertResults] = useState(null);
  const [savingAlerts, setSavingAlerts] = useState(false);
  const [settingsError, setSettingsError] = useState("");

  const loadSettings = useCallback(async () => {
    setSettingsError("");
    try {
      const [st, al] = await Promise.all([api.get("/biometric/employees"), api.get("/biometric/alerts")]);
      setStaff(st.data.employees);
      setStaffEdits({});
      setAlertCfg(al.data);
      setAlertForm({ ...al.data.settings, numbersText: al.data.settings.numbers.join("\n") });
    } catch (err) {
      setSettingsError(err.response?.data?.error || "Could not load the settings");
    }
  }, []);

  useEffect(() => {
    if (tab === "settings" && isAdmin) loadSettings();
  }, [tab, loadSettings, isAdmin]);

  const editStaff = (id, field, value) =>
    setStaffEdits((all) => ({ ...all, [id]: { ...(all[id] || {}), [field]: value } }));

  const saveStaff = async (emp) => {
    const changes = staffEdits[emp.id];
    if (!changes) return;
    setSavingStaff(emp.id);
    try {
      await api.patch(`/biometric/employees/${emp.id}`, changes);
      notify(`Saved #${String(emp.id).padStart(2, "0")} ${changes.name !== undefined ? changes.name || emp.machineName : emp.name}`);
      setMonth(null); // names and dates change the month: rebuild on next open
      loadSettings();
    } catch (err) {
      notify(err.response?.data?.error || "Could not save", "error");
    } finally {
      setSavingStaff(null);
    }
  };

  const saveAlerts = async () => {
    setSavingAlerts(true);
    try {
      const numbers = alertForm.numbersText.split(/[\n,]+/).map((n) => n.trim()).filter(Boolean);
      await api.put("/biometric/alerts", {
        enabled: alertForm.enabled, numbers, morningTime: alertForm.morningTime, eveningTime: alertForm.eveningTime,
      });
      notify("WhatsApp summary settings saved");
      loadSettings();
    } catch (err) {
      notify(err.response?.data?.error || "Could not save", "error");
    } finally {
      setSavingAlerts(false);
    }
  };

  const previewAlert = async (kind) => {
    try {
      const res = await api.get("/biometric/alerts/preview", { params: { kind } });
      setAlertPreview({ kind, text: res.data.text });
      setAlertResults(null);
    } catch (err) {
      notify(err.response?.data?.error || "Could not build the message", "error");
    }
  };

  const sendAlertNow = async (kind) => {
    if (!window.confirm(`Send today's ${kind} summary now to all saved numbers?`)) return;
    try {
      const res = await api.post("/biometric/alerts/send-now", { kind });
      setAlertResults(res.data.results);
      const ok = res.data.results.filter((r) => r.ok).length;
      notify(`Sent to ${ok} of ${res.data.results.length}`, ok === res.data.results.length ? "ok" : "error");
    } catch (err) {
      notify(err.response?.data?.error || "Could not send", "error");
    }
  };

  // ---- Calendar (holidays and special working days) ----
  const [calYm, setCalYm] = useState(thisMonth());
  const [cal, setCal] = useState(null);
  const [calError, setCalError] = useState("");
  const [calEdit, setCalEdit] = useState(null); // the calendar day being changed
  const [calForm, setCalForm] = useState({ kind: "", note: "" });
  const [savingCal, setSavingCal] = useState(false);

  const loadCal = useCallback(async () => {
    setCalError("");
    try {
      const res = await api.get(`/biometric/calendar/${calYm}`);
      setCal(res.data);
    } catch (err) {
      setCal(null);
      setCalError(err.response?.data?.error || "Could not load the calendar");
    }
  }, [calYm]);

  useEffect(() => {
    if (tab === "calendar" && isAdmin) loadCal();
  }, [tab, loadCal, isAdmin]);

  const openCalDay = (d) => {
    if (!isAdmin) return;
    const kind = d.kind === "holiday" ? (d.paid ? "holiday-paid" : "holiday-unpaid") : d.kind;
    setCalForm({ kind, note: d.note || "" });
    setCalEdit(d);
  };

  const saveCalDay = async () => {
    if (!calEdit) return;
    const kind = calForm.kind.startsWith("holiday") ? "holiday" : calForm.kind;
    setSavingCal(true);
    try {
      await api.put(`/biometric/calendar/${calEdit.date}`, { kind, paid: calForm.kind === "holiday-paid", note: calForm.note });
      setCalEdit(null);
      notify(`Saved ${prettyDate(calEdit.date)}`);
      loadCal();
      setMonth(null); // month totals depend on the calendar: rebuild on next open
    } catch (err) {
      notify(err.response?.data?.error || "Could not save the day", "error");
    } finally {
      setSavingCal(false);
    }
  };

  useEffect(() => {
    if (tab === "month" && isAdmin) loadMonth();
  }, [tab, loadMonth, isAdmin]);

  const monthMp = useMemo(() => (month ? month.data.reduce((n, e) => n + (+e.missingPunch || 0), 0) : 0), [month]);

  const monthRows = useMemo(() => {
    if (!month) return [];
    const q = monthSearch.trim().toLowerCase();
    return month.data.filter((e) => !q || e.name.toLowerCase().includes(q) || e.empCode === q);
  }, [month, monthSearch]);

  const exportMonth = () => {
    if (!month) return;
    const rows = month.data.map((e) => {
      const row = { "Emp Code": e.empCode, Name: e.name };
      e.days.forEach((d) => { row[String(d.day).padStart(2, "0")] = d.status; });
      Object.assign(row, {
        Present: e.present, "Half Days": e.hl, "Weekly Off": e.wo, Absent: e.absent, "Missing Punch": e.missingPunch, Leave: e.leave,
        Holidays: e.holidays, "Paid Days": e.paidDays, "Late Hrs": e.lateHrs, "Total Work Hrs": e.totalWorkHrs, "OT (policy)": e.totalCalculatedOt, "Short Hrs": e.totalShortHrs,
      });
      return row;
    });
    const detail = [];
    month.data.forEach((e) => e.days.forEach((d) => {
      if (d.status) detail.push({
        "Emp Code": e.empCode, Name: e.name, Day: d.day, Status: d.status,
        Arrive: d.arrive, Depart: d.depart, "Work Hrs": d.workHrs, "Time Away": d.awayHrs || "", "OT (policy)": d.calculatedOt,
        Punches: (d.punchTimes || []).join(" "),
        Corrected: d.edited ? `${d.edited.by} ${d.edited.at}` : "", Note: d.edited ? d.edited.note : "",
      });
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "Summary");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detail), "Day detail");
    XLSX.writeFile(wb, `Live_Attendance_${month.ym}.xlsx`);
    notify("Excel downloaded");
  };

  // Hand the month to the existing Attendance Manager (same data shape, same save + version history).
  const saveAsPayrollSheet = async () => {
    if (!month) return;
    setSaving(true);
    try {
      let baseVersion;
      let exists = false;
      try {
        const v = await api.get(`/attendance/months/${encodeURIComponent(month.month_year)}/version`);
        baseVersion = v.data.version;
        exists = true;
      } catch (err) {
        if (err.response?.status !== 404) throw err;
      }
      const mpWarning = monthMp > 0
        ? `WARNING: ${monthMp} day(s) still have a missing punch (MP). They will be saved as unpaid. Fix them first if they should be paid.\n\n`
        : "";
      const msg = mpWarning + (exists
        ? `"${month.month_year}" is already saved in the Attendance Manager.\n\nSaving REPLACES it with the biometric data (any manual edits, leaves or corrections made there will be lost from the current version). The previous version is kept in its history and can be restored.\n\nReplace it?`
        : `Save "${month.month_year}" to the Attendance Manager as a new month?`);
      if (!window.confirm(msg)) return;
      await api.post("/attendance/save", {
        month_year: month.month_year, data: month.data, base_version: baseVersion,
      });
      notify(`Saved ${month.month_year}. Open "Attendance" to edit leaves and OT.`);
    } catch (err) {
      notify(err.response?.data?.error || "Could not save the month", "error");
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------- render
  const minsSinceLast = live?.lastPunch ? minutesAgo(live.lastPunch.receivedAt) : null;

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-slate-50 py-16 px-4">
        <div className="max-w-md mx-auto bg-white rounded-2xl shadow-sm border border-slate-200 p-6 text-center">
          <i className="fas fa-lock text-3xl text-slate-400 mb-3"></i>
          <h1 className="text-lg font-bold text-slate-800">Admins only</h1>
          <p className="text-sm text-slate-500 mt-1">Live Attendance can only be opened by an admin.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4 sm:px-6 lg:px-8 pb-24">
      {toast && (
        <div className={`fixed top-20 right-4 z-[120] px-4 py-3 rounded-xl shadow-2xl text-white font-medium ${toast.type === "error" ? "bg-rose-600" : "bg-emerald-600"}`}>
          {toast.msg}
        </div>
      )}

      <div className="max-w-7xl mx-auto space-y-5">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-emerald-600 text-white flex items-center justify-center text-xl shadow-md">
                <i className="fas fa-fingerprint"></i>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-800">Live Attendance</h1>
                <p className="text-sm text-slate-500">Who punched and who didn't, straight from the factory biometric machine.</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {tab === "live" && (
                <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border ${liveError ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                  <span className={`w-2 h-2 rounded-full ${liveError ? "bg-rose-500" : "bg-emerald-500 animate-pulse"}`}></span>
                  {liveError ? "Offline - retrying" : date === todayStr() ? "LIVE" : "History"}
                  {updatedAt && !liveError && <span className="text-slate-400 font-normal">updated {updatedAt.toLocaleTimeString("en-GB")}</span>}
                </span>
              )}
              <div className="flex rounded-xl border border-slate-200 overflow-hidden text-sm font-semibold">
                <button onClick={() => setTab("live")} className={`px-4 py-2 ${tab === "live" ? "bg-slate-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>Day</button>
                <button onClick={() => setTab("month")} className={`px-4 py-2 ${tab === "month" ? "bg-slate-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>Month</button>
                <button onClick={() => setTab("calendar")} className={`px-4 py-2 ${tab === "calendar" ? "bg-slate-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>Calendar</button>
                <button onClick={() => setTab("settings")} className={`px-4 py-2 ${tab === "settings" ? "bg-slate-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`} title="Staff names and dates, WhatsApp summary">
                  <i className="fas fa-gear"></i><span className="hidden sm:inline ml-1">Settings</span>
                </button>
              </div>
              <button onClick={() => setTab("attention")}
                className={`px-4 py-2 rounded-xl border text-sm font-semibold flex items-center gap-2 ${tab === "attention" ? "bg-amber-600 text-white border-amber-600" : "bg-white text-amber-700 border-amber-300 hover:bg-amber-50"}`}>
                <i className="fas fa-triangle-exclamation"></i>Needs attention
                {attention && attention.count > 0 && (
                  <span className={`px-1.5 rounded-full text-xs ${tab === "attention" ? "bg-white text-amber-700" : "bg-amber-600 text-white"}`}>{attention.count}</span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* ================= DAY ================= */}
        {tab === "live" && (
          <>
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 flex flex-wrap items-center gap-3">
              <button onClick={() => setDate(shiftDate(date, -1))} className="px-3 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-semibold"><i className="fas fa-chevron-left mr-1"></i>Prev</button>
              <input type="date" value={date} max={todayStr()} onChange={(e) => e.target.value && setDate(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700" />
              <button onClick={() => setDate(shiftDate(date, 1))} disabled={date >= todayStr()} className="px-3 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-semibold disabled:opacity-40">Next<i className="fas fa-chevron-right ml-1"></i></button>
              {date !== todayStr() && <button onClick={() => setDate(todayStr())} className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold">Today</button>}
              <span className="text-slate-500 text-sm ml-1">{prettyDate(date)}</span>
              <label className="ml-auto flex items-center gap-2 text-xs text-slate-500">
                <input type="checkbox" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} /> show hidden staff
              </label>
            </div>

            {liveError && !live && (
              <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl p-4 text-sm">{liveError}</div>
            )}

            {live && (
              <>
                {live.weeklyOff && (
                  <div className="bg-slate-100 border border-slate-200 text-slate-600 rounded-xl p-3 text-sm">
                    <i className="fas fa-umbrella-beach mr-2"></i>Weekly off. Anyone listed as punched is working on the off day.
                    {isAdmin && " If the factory is open today, mark it as a working day in the Calendar tab."}
                  </div>
                )}
                {live.holiday && (
                  <div className="bg-teal-50 border border-teal-200 text-teal-700 rounded-xl p-3 text-sm">
                    <i className="fas fa-gift mr-2"></i>Holiday{live.holiday.note ? `: ${live.holiday.note}` : ""} ({live.holiday.paid ? "paid" : "unpaid"}). Anyone listed as punched is working on the holiday.
                  </div>
                )}
                {live.specialWorkingDay && (
                  <div className="bg-indigo-50 border border-indigo-200 text-indigo-700 rounded-xl p-3 text-sm">
                    <i className="fas fa-industry mr-2"></i>Special working day{live.specialWorkingDay.note ? `: ${live.specialWorkingDay.note}` : ""}. Staff who have not punched count as absent.
                  </div>
                )}
                {live.kpis.leave > 0 && (
                  <div className="text-sm text-violet-700"><i className="fas fa-plane-departure mr-2"></i>{live.kpis.leave} on leave today (not counted as "Not punched").</div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {[
                    ["Total staff", live.kpis.total, "text-slate-800", "border-slate-300", "all"],
                    ["Punched", live.kpis.present, "text-emerald-600", "border-emerald-400", "punched"],
                    ["Not punched", live.kpis.notPunched, "text-rose-600", "border-rose-400", "not_punched"],
                    ["Inside now", live.kpis.in, "text-emerald-700", "border-emerald-300", "in"],
                    ["Left", live.kpis.out, "text-sky-600", "border-sky-400", "out"],
                  ].map(([label, value, color, border, key]) => (
                    <button key={label} onClick={() => setFilter(key)} className={`text-left bg-white rounded-xl border-l-4 ${border} shadow-sm p-4 hover:shadow-md transition ${filter === key ? "ring-2 ring-slate-300" : ""}`}>
                      <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
                      <div className={`text-3xl font-bold ${color}`}>{value}</div>
                    </button>
                  ))}
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                  <div className="p-4 flex flex-wrap items-center gap-3 border-b border-slate-100">
                    <div className="flex flex-wrap gap-2">
                      {[["all", "All"], ["punched", "Punched"], ["not_punched", "Not punched"], ["in", "Inside"], ["out", "Left"]].map(([k, l]) => (
                        <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${filter === k ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{l}</button>
                      ))}
                    </div>
                    <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or ID" className="ml-auto px-3 py-2 rounded-lg border border-slate-200 text-sm w-56" />
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                        <tr>
                          <th className="text-left px-4 py-3">ID</th>
                          <th className="text-left px-4 py-3">Employee</th>
                          <th className="text-left px-4 py-3">Status</th>
                          <th className="text-left px-4 py-3">First in</th>
                          <th className="text-left px-4 py-3">Last punch</th>
                          <th className="text-left px-4 py-3">Punches</th>
                          {isAdmin && <th className="px-4 py-3"></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {visibleEmployees.map((e) => {
                          const meta = STATE_META[e.state];
                          return (
                            <tr key={e.id} className={`border-t border-slate-100 hover:bg-slate-50 ${e.active ? "" : "opacity-50"}`}>
                              <td className="px-4 py-3 text-slate-500 font-mono">#{String(e.id).padStart(2, "0")}</td>
                              <td className="px-4 py-3 font-semibold text-slate-800">{e.name}{!e.active && <span className="ml-2 text-xs text-slate-400">(hidden)</span>}</td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${meta.cls}`}>
                                  <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`}></span>{meta.label}
                                </span>
                                {e.late && <span className="ml-2 text-xs font-semibold text-amber-600">late</span>}
                                {e.missingOut && <span className="ml-2 text-xs font-semibold text-amber-600">no out punch</span>}
                              </td>
                              <td className="px-4 py-3 font-mono text-slate-700">{e.first || "--"}</td>
                              <td className="px-4 py-3 font-mono text-slate-700">{e.last || "--"}</td>
                              <td className="px-4 py-3" title={`${e.count} punches: ${e.punches.join("  ")}`}>
                                {(e.moves || []).length === 0 ? <span className="text-slate-400">0</span> : (
                                  <div className="flex flex-wrap gap-1">
                                    {e.moves.map((m, i) => (
                                      <span key={i} className={`px-1.5 py-0.5 rounded text-xs font-mono whitespace-nowrap ${m.dir === "in" ? "bg-emerald-50 text-emerald-700" : "bg-sky-50 text-sky-700"}`}>
                                        {m.dir === "out" ? "Out" : i === 0 ? "In" : "Back"} {m.time}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </td>
                              {isAdmin && (
                                <td className="px-4 py-3 text-right">
                                  <button onClick={() => toggleActive(e)} className="text-xs text-slate-400 hover:text-rose-600" title={e.active ? "Hide (left the company)" : "Show again"}>
                                    <i className={`fas ${e.active ? "fa-eye-slash" : "fa-eye"}`}></i>
                                  </button>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                        {visibleEmployees.length === 0 && (
                          <tr><td colSpan={isAdmin ? 7 : 6} className="px-4 py-10 text-center text-slate-400">No one matches this filter.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="px-4 py-3 text-xs text-slate-400 border-t border-slate-100 flex flex-wrap gap-x-4 gap-y-1">
                    <span>Showing {visibleEmployees.length} of {live.employees.length}</span>
                    {live.lastPunch && (
                      <span>
                        Last punch from the machine: {live.lastPunch.time.slice(0, 5)} on {live.lastPunch.date}
                        {minsSinceLast !== null && date === todayStr() && ` (received ${minsSinceLast} min ago)`}
                      </span>
                    )}
                    <span>"Inside" = punched in, not yet out. "Left" = a later punch at least 1 hour after the first.</span>
                  </div>
                </div>
              </>
            )}
            {!live && !liveError && <div className="text-center text-slate-400 py-16">Loading...</div>}
          </>
        )}

        {/* ================= MONTH ================= */}
        {tab === "month" && (
          <>
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 flex flex-wrap items-center gap-3">
              <input type="month" value={ym} max={thisMonth()} onChange={(e) => e.target.value && setYm(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700" />
              <button onClick={() => loadMonth()} className="px-3 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-semibold"><i className="fas fa-rotate mr-1"></i>Refresh</button>
              <input value={monthSearch} onChange={(e) => setMonthSearch(e.target.value)} placeholder="Search name or code" className="px-3 py-2 rounded-lg border border-slate-200 text-sm w-52" />
              <div className="ml-auto flex gap-2">
                <button onClick={exportMonth} disabled={!month} className="px-4 py-2 rounded-lg bg-slate-800 text-white text-sm font-semibold disabled:opacity-40"><i className="fas fa-file-excel mr-2"></i>Excel</button>
                {isAdmin && (
                  <button onClick={saveAsPayrollSheet} disabled={!month || saving} className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold disabled:opacity-40" title="Send this month to the Attendance Manager for leaves, OT and payroll">
                    <i className="fas fa-paper-plane mr-2"></i>{saving ? "Saving..." : "Save to Attendance Manager"}
                  </button>
                )}
              </div>
            </div>

            {monthError && <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl p-4 text-sm">{monthError}</div>}
            {monthLoading && <div className="text-center text-slate-400 py-16">Loading month...</div>}
            {month && !monthLoading && monthMp > 0 && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-3 text-sm">
                <i className="fas fa-triangle-exclamation mr-2"></i>
                {monthMp} day{monthMp === 1 ? "" : "s"} with a missing punch (MP) this month. They are not paid until fixed:
                {isAdmin ? " click the amber day and add the missing In / Out time, or choose a status." : " an admin needs to correct them."}
              </div>
            )}

            {month && !monthLoading && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center gap-4 text-sm">
                  <span className="font-bold text-slate-800">{month.month_year}</span>
                  <span className="text-slate-500">{month.count} employees</span>
                  <span className="flex items-center gap-3 text-xs">
                    {[["P", "Present"], ["P/2", "Half day"], ["A", "Absent"], ["L", "Leave"], ["WO", "Weekly off"], ["H", "Holiday"]].map(([k, l]) => (
                      <span key={k} className="flex items-center gap-1"><span className={`inline-block w-4 h-4 rounded ${CELL_CLS[k]}`}></span>{l}</span>
                    ))}
                    <span className="flex items-center gap-1"><span className={`inline-block w-4 h-4 rounded ring-2 ring-amber-400 ${CELL_CLS.MP}`}></span>MP = missing punch, not paid until fixed</span>
                    <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-indigo-500"></span>Corrected by hand</span>
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="text-xs border-collapse">
                    <thead className="bg-slate-50 text-slate-500">
                      <tr>
                        <th className="sticky left-0 z-10 bg-slate-50 text-left px-3 py-2 min-w-[190px]">Employee</th>
                        {(month.days || Array.from({ length: month.daysInMonth }, (_, i) => ({ day: i + 1 }))).map((h) => {
                          const isToday = month.today === `${month.ym}-${String(h.day).padStart(2, "0")}`;
                          return (
                            <th key={h.day}
                              className={`px-1 py-1 w-8 text-center font-semibold leading-tight ${h.off === "H" ? "bg-teal-50 text-teal-700" : h.off === "WO" ? "bg-slate-100 text-slate-400" : h.special === "working" ? "bg-indigo-50 text-indigo-700" : ""} ${isToday ? "border-b-2 border-emerald-500" : ""}`}
                              title={[
                                isToday ? "Today - still in progress, statuses update as people punch" : "",
                                h.off === "H" ? `Holiday${h.note ? `: ${h.note}` : ""}` : h.special === "working" ? `Special working day${h.note ? `: ${h.note}` : ""}` : h.off === "WO" ? "Weekly off" : "",
                              ].filter(Boolean).join(". ") || undefined}>
                              {h.day}<div className={`text-[9px] ${isToday ? "font-bold text-emerald-600" : "font-normal"}`}>{isToday ? "today" : h.weekday ? h.weekday.slice(0, 2) : ""}</div>
                            </th>
                          );
                        })}
                        <th className="px-2 py-2">Pres</th><th className="px-2 py-2">Abs</th><th className="px-2 py-2" title="Missing punch days, not paid until fixed">MP</th><th className="px-2 py-2">Leave</th><th className="px-2 py-2">WO</th><th className="px-2 py-2">Hol</th><th className="px-2 py-2">Late</th><th className="px-2 py-2">Work hrs</th><th className="px-2 py-2">OT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthRows.map((e) => (
                        <tr key={e.empCode} className="border-t border-slate-100">
                          <td className="sticky left-0 z-10 bg-white px-3 py-1.5 font-semibold text-slate-700 whitespace-nowrap"><span className="text-slate-400 font-mono mr-2">{e.empCode}</span>{e.name}</td>
                          {e.days.map((d) => (
                            <td key={d.day} className="p-0.5">
                              <button
                                type="button"
                                onClick={() => openCell(e, d, `${month.ym}-${String(d.day).padStart(2, "0")}`)}
                                className={`relative h-6 w-7 rounded flex items-center justify-center font-semibold hover:ring-2 hover:ring-slate-400 ${CELL_CLS[d.status] || CELL_CLS[""]} ${d.needsFix ? "ring-2 ring-amber-400" : ""}`}
                                title={`${e.name} - day ${d.day}: ${d.status || "-"}${d.arrive ? `  in ${d.arrive}` : ""}${d.depart ? `  out ${d.depart}` : ""}${d.workHrs ? `  worked ${d.workHrs}` : ""}${d.awayHrs ? ` (away ${d.awayHrs})` : ""}${d.calculatedOt !== "00:00" ? `  OT ${d.calculatedOt}` : ""}${d.missingOut ? "  (no out punch)" : ""}${d.missingIn ? "  (no in punch)" : ""}${d.oddPunches ? "  (a punch is missing)" : ""}${d.badTimes ? "  (out before in)" : ""}${d.manyPunches ? "  (too many punches, please check)" : ""}${d.edited ? `  - corrected${d.edited.note ? `: ${d.edited.note}` : ""}` : ""}`}
                              >
                                {d.status === "P/2" ? "½" : d.status}
                                {d.edited && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-indigo-500 ring-1 ring-white"></span>}
                              </button>
                            </td>
                          ))}
                          <td className="px-2 text-center font-bold text-emerald-700">{e.present}</td>
                          <td className="px-2 text-center font-bold text-rose-600">{e.absent}</td>
                          <td className={`px-2 text-center font-bold ${+e.missingPunch > 0 ? "text-amber-600" : "text-slate-300"}`}>{e.missingPunch}</td>
                          <td className="px-2 text-center text-violet-600">{e.leave}</td>
                          <td className="px-2 text-center text-slate-500">{e.wo}</td>
                          <td className="px-2 text-center text-teal-600">{e.holidays}</td>
                          <td className="px-2 text-center font-mono text-amber-600">{e.lateHrs}</td>
                          <td className="px-2 text-center font-mono text-slate-600">{e.totalWorkHrs}</td>
                          <td className="px-2 text-center font-mono text-indigo-600">{e.totalCalculatedOt}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="px-4 py-3 text-xs text-slate-400 border-t border-slate-100">
                  Built from the biometric punches with the company rules (late after 08:30, OT after 17:30 counted from 17:00 minus late arrival,
                  half day if worked 4:30 or less). Work hours count time inside only: time away between an out and a back-in is not counted. Today updates as people punch: after 09:30 anyone not punched shows A until they arrive. Days after today stay blank. Click any day to see its punches{isAdmin ? " and correct it (leave, missing punch, status, OT)" : ""};
                  corrections are kept separately, so new punches never overwrite them.
                </div>
              </div>
            )}
          </>
        )}

        {/* ================= NEEDS ATTENTION ================= */}
        {tab === "attention" && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 flex flex-wrap items-center gap-3 border-b border-slate-100 bg-amber-50">
              <span className="font-semibold text-amber-800">
                <i className="fas fa-triangle-exclamation mr-2"></i>
                {attention ? `${attention.count} punch problem${attention.count === 1 ? "" : "s"}` : "Checking..."}
              </span>
              <select value={attnDays} onChange={(e) => setAttnDays(Number(e.target.value))} className="px-2 py-1 rounded-lg border border-amber-200 bg-white text-xs text-amber-800">
                <option value={7}>last 7 days</option>
                <option value={31}>last 31 days</option>
              </select>
              <span className="text-xs text-amber-700">{isAdmin ? "Click Fix to correct a day. A fixed day leaves this list." : "An admin can correct these days."}</span>
            </div>
            {attention && attention.count === 0 && (
              <div className="px-4 py-12 text-center text-emerald-700 text-sm"><i className="fas fa-circle-check mr-2"></i>All clear: no punch problems in the last {attnDays} days.</div>
            )}
            {attention && attention.items.map((it) => (
              <div key={`${it.date}-${it.employeeId}`} className="px-4 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-slate-100 text-sm">
                <span className="text-xs font-semibold text-slate-500 w-24">{shortDate(it.date)}</span>
                <span className="font-semibold text-slate-800 min-w-[10rem]"><span className="text-slate-400 font-mono mr-1">{it.empCode}</span>{it.name}</span>
                <span className="text-amber-700">{it.problem}</span>
                <span className="flex flex-wrap gap-1">{it.day.punchTimes.map((t, i) => <span key={i} className="px-1.5 py-0.5 rounded bg-slate-100 font-mono text-xs">{t}</span>)}</span>
                <button onClick={() => openCell({ employeeId: it.employeeId, name: it.name, empCode: it.empCode }, it.day, it.date)} className="ml-auto px-3 py-1 rounded-lg bg-amber-600 text-white text-xs font-semibold">
                  {isAdmin ? "Fix" : "View"}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* ================= SETTINGS ================= */}
        {tab === "settings" && (
          <>
            {settingsError && <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl p-4 text-sm">{settingsError}</div>}
            {!staff && !settingsError && <div className="text-center text-slate-400 py-16">Loading settings...</div>}

            {staff && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100">
                  <div className="font-bold text-slate-800"><i className="fas fa-users mr-2 text-slate-400"></i>Staff</div>
                  <p className="text-xs text-slate-500 mt-1">
                    A new employee appears here by themselves after their first punch on the machine (as "Employee #number") - type their name here.
                    The joining date is their first punch unless you set one. Days before joining and after leaving stay blank, not absent.
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                      <tr>
                        <th className="text-left px-3 py-2">ID</th>
                        <th className="text-left px-3 py-2">Name</th>
                        <th className="text-left px-3 py-2">Joined</th>
                        <th className="text-left px-3 py-2">Left</th>
                        <th className="text-left px-3 py-2">Last punch</th>
                        <th className="text-left px-3 py-2">Shown</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {staff.map((e) => {
                        const ed = staffEdits[e.id] || {};
                        const val = (k, fallback) => (ed[k] !== undefined ? ed[k] : fallback);
                        return (
                          <tr key={e.id} className={`border-t border-slate-100 ${val("active", e.active) ? "" : "opacity-60"}`}>
                            <td className="px-3 py-2 font-mono text-slate-500">#{String(e.id).padStart(2, "0")}</td>
                            <td className="px-3 py-2">
                              <input value={val("name", e.displayName)} placeholder={e.displayName ? e.machineName : e.name} maxLength={120}
                                onChange={(ev) => editStaff(e.id, "name", ev.target.value)}
                                className="w-48 px-2 py-1 rounded border border-slate-200 text-sm" />
                              {e.displayName && e.machineName !== e.displayName && <div className="text-[10px] text-slate-400 mt-0.5">machine: {e.machineName}</div>}
                            </td>
                            <td className="px-3 py-2">
                              <input type="date" value={val("joinDate", e.joinDate)} onChange={(ev) => editStaff(e.id, "joinDate", ev.target.value)}
                                className="px-2 py-1 rounded border border-slate-200 text-sm" />
                              {!val("joinDate", e.joinDate) && e.firstPunch && <div className="text-[10px] text-slate-400 mt-0.5">auto: first punch {e.firstPunch}</div>}
                            </td>
                            <td className="px-3 py-2">
                              <input type="date" value={val("leaveDate", e.leaveDate)} onChange={(ev) => editStaff(e.id, "leaveDate", ev.target.value)}
                                className="px-2 py-1 rounded border border-slate-200 text-sm" />
                            </td>
                            <td className="px-3 py-2 text-xs text-slate-500 font-mono">{e.lastPunch || "--"}</td>
                            <td className="px-3 py-2">
                              <input type="checkbox" checked={val("active", e.active)} onChange={(ev) => editStaff(e.id, "active", ev.target.checked)} />
                            </td>
                            <td className="px-3 py-2 text-right">
                              {staffEdits[e.id] && (
                                <button onClick={() => saveStaff(e)} disabled={savingStaff === e.id} className="px-3 py-1 rounded-lg bg-indigo-600 text-white text-xs font-semibold disabled:opacity-40">
                                  {savingStaff === e.id ? "Saving..." : "Save"}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {alertForm && alertCfg && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="font-bold text-slate-800"><i className="fab fa-whatsapp mr-2 text-emerald-600"></i>WhatsApp daily summary</div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${alertCfg.whatsapp === "CONNECTED" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
                    WhatsApp on the server: {alertCfg.whatsapp === "CONNECTED" ? "connected" : alertCfg.whatsapp.toLowerCase().replace("_", " ")}
                  </span>
                </div>
                <p className="text-xs text-slate-500">
                  Morning: who is present, absent, late and on leave. Evening: who has not punched out yet.
                  Not sent on weekly offs and holidays (it is sent on a Tuesday marked as a working day).
                  {alertCfg.whatsapp !== "CONNECTED" && " WhatsApp is not connected on the server, so nothing can be sent until it is - connect it on the WhatsApp page (/whatsapp)."}
                </p>
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <input type="checkbox" checked={alertForm.enabled} onChange={(ev) => setAlertForm((f) => ({ ...f, enabled: ev.target.checked }))} />
                  Send the summary every day
                </label>
                <div className="grid sm:grid-cols-3 gap-3">
                  <label className="block sm:row-span-2">
                    <span className="text-xs text-slate-500">Mobile numbers (one per line, up to 10)</span>
                    <textarea rows={4} value={alertForm.numbersText} onChange={(ev) => setAlertForm((f) => ({ ...f, numbersText: ev.target.value }))}
                      placeholder={"98250 12345\n99985 46224"} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-mono" />
                  </label>
                  <label className="block">
                    <span className="text-xs text-slate-500">Morning message at</span>
                    <input type="time" value={alertForm.morningTime} onChange={(ev) => setAlertForm((f) => ({ ...f, morningTime: ev.target.value }))}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                  </label>
                  <label className="block">
                    <span className="text-xs text-slate-500">Evening message at</span>
                    <input type="time" value={alertForm.eveningTime} onChange={(ev) => setAlertForm((f) => ({ ...f, eveningTime: ev.target.value }))}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                  </label>
                  <div className="sm:col-span-2 text-xs text-slate-400">
                    Last sent: morning {alertCfg.lastSent.morning || "never"}, evening {alertCfg.lastSent.evening || "never"}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={saveAlerts} disabled={savingAlerts} className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold disabled:opacity-40">{savingAlerts ? "Saving..." : "Save"}</button>
                  <button onClick={() => previewAlert("morning")} className="px-3 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Preview morning</button>
                  <button onClick={() => previewAlert("evening")} className="px-3 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Preview evening</button>
                </div>
                {alertPreview && (
                  <div className="border border-slate-200 rounded-xl p-3 bg-slate-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-slate-500 uppercase">{alertPreview.kind} message (today)</span>
                      <button onClick={() => sendAlertNow(alertPreview.kind)} disabled={!alertCfg.settings.numbers.length}
                        className="px-3 py-1 rounded-lg bg-emerald-600 text-white text-xs font-semibold disabled:opacity-40" title="Sends to the saved numbers">
                        Send this now to the saved numbers
                      </button>
                    </div>
                    <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans">{alertPreview.text}</pre>
                    {alertResults && (
                      <ul className="mt-2 text-xs">
                        {alertResults.map((r) => (
                          <li key={r.number} className={r.ok ? "text-emerald-700" : "text-rose-600"}>{r.number}: {r.ok ? "sent" : r.error}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* ================= CALENDAR ================= */}
        {tab === "calendar" && (
          <>
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 flex flex-wrap items-center gap-3">
              <input type="month" value={calYm} onChange={(e) => e.target.value && setCalYm(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700" />
              <span className="text-sm text-slate-500">
                Weekly off: <b>{cal ? cal.weeklyOffDay : "..."}</b>. {isAdmin ? "Click a day to mark a holiday, or to open the factory on a weekly off." : "Only an admin can change days."}
              </span>
            </div>
            {calError && <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl p-4 text-sm">{calError}</div>}
            {!cal && !calError && <div className="text-center text-slate-400 py-16">Loading calendar...</div>}
            {cal && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-3 sm:p-4">
                <div className="grid grid-cols-7 gap-1 sm:gap-2 text-center text-[11px] sm:text-xs font-semibold text-slate-500 mb-1">
                  {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((w) => <div key={w}>{w}</div>)}
                </div>
                <div className="grid grid-cols-7 gap-1 sm:gap-2">
                  {Array.from({ length: (new Date(cal.days[0].date + "T00:00:00").getDay() + 6) % 7 }, (_, i) => <div key={`b${i}`}></div>)}
                  {cal.days.map((d) => {
                    const look = d.kind === "holiday" ? "bg-teal-50 border-teal-300 text-teal-800"
                      : d.kind === "working" ? "bg-indigo-50 border-indigo-300 text-indigo-800"
                      : d.weeklyOff ? "bg-slate-100 border-slate-200 text-slate-500" : "bg-white border-slate-200 text-slate-700";
                    const label = d.kind === "holiday" ? `Holiday${d.paid ? " (paid)" : " (unpaid)"}`
                      : d.kind === "working" ? "Working day" : d.weeklyOff ? "Weekly off" : "";
                    return (
                      <button key={d.date} type="button" onClick={() => openCalDay(d)} disabled={!isAdmin}
                        className={`min-h-[64px] sm:min-h-[84px] rounded-lg border p-1 sm:p-2 text-left align-top ${look} ${isAdmin ? "hover:ring-2 hover:ring-slate-300" : "cursor-default"} ${d.date === cal.today ? "ring-2 ring-emerald-400" : ""}`}
                        aria-label={`${d.day} ${label}${d.note ? ` - ${d.note}` : ""}`}
                        title={`${d.note || label || ""}${d.by ? ` (set by ${d.by} on ${d.at})` : ""}` || undefined}>
                        <div className="font-bold text-sm">{d.day}</div>
                        {label && <div className="text-[10px] sm:text-xs font-semibold leading-tight">{label}</div>}
                        {d.note && <div className="hidden sm:block text-xs leading-tight opacity-80 break-words">{d.note}</div>}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-3 text-xs text-slate-400">
                  Holiday: staff who do not punch get H (paid holidays count in paid days). Working day: the factory opens on its weekly off, so staff who do not punch are absent.
                </div>
              </div>
            )}
          </>
        )}

        {/* ================= CALENDAR DAY ================= */}
        {calEdit && (
          <div className="fixed inset-0 z-[100] bg-slate-900/40 flex items-center justify-center p-4" onClick={() => !savingCal && setCalEdit(null)}>
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm" onClick={(ev) => ev.stopPropagation()}>
              <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
                <div>
                  <div className="font-bold text-slate-800">{prettyDate(calEdit.date)}</div>
                  <div className="text-sm text-slate-500">{calEdit.weeklyOff ? "Normally the weekly off" : "Normally a working day"}</div>
                </div>
                <button onClick={() => setCalEdit(null)} className="text-slate-400 hover:text-slate-700" title="Close"><i className="fas fa-xmark text-lg"></i></button>
              </div>
              <div className="px-5 py-4 space-y-3 text-sm">
                <label className="block">
                  <span className="text-xs text-slate-500">This day is</span>
                  <select value={calForm.kind} onChange={(ev) => setCalForm((f) => ({ ...f, kind: ev.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm">
                    {CAL_KIND_OPTIONS.filter(([v]) => v !== "working" || calEdit.weeklyOff).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs text-slate-500">Note</span>
                  <input value={calForm.note} maxLength={120} onChange={(ev) => setCalForm((f) => ({ ...f, note: ev.target.value }))} placeholder="e.g. Diwali, or Festival - factory open" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                </label>
              </div>
              <div className="px-5 py-4 border-t border-slate-100 flex justify-end gap-2">
                <button onClick={() => setCalEdit(null)} disabled={savingCal} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
                <button onClick={saveCalDay} disabled={savingCal} className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold disabled:opacity-40">{savingCal ? "Saving..." : "Save"}</button>
              </div>
            </div>
          </div>
        )}

        {/* ================= CORRECT ONE DAY ================= */}
        {editCell && (() => {
          const { emp, day, dateStr } = editCell;
          const field = (key, props) => (
            <input {...props} value={fixForm[key]} disabled={!isAdmin}
              onChange={(ev) => setFixForm((f) => ({ ...f, [key]: ev.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm disabled:bg-slate-50" />
          );
          return (
            <div className="fixed inset-0 z-[100] bg-slate-900/40 flex items-center justify-center p-4" onClick={() => !savingFix && setEditCell(null)}>
              <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(ev) => ev.stopPropagation()}>
                <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
                  <div>
                    <div className="font-bold text-slate-800">{emp.name} <span className="text-slate-400 font-mono text-sm">#{emp.empCode}</span></div>
                    <div className="text-sm text-slate-500">{prettyDate(dateStr)}</div>
                  </div>
                  <button onClick={() => setEditCell(null)} className="text-slate-400 hover:text-slate-700" title="Close"><i className="fas fa-xmark text-lg"></i></button>
                </div>

                <div className="px-5 py-4 space-y-4 text-sm">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1">Machine punches</div>
                    {(day.punchTimes || []).length === 0
                      ? <span className="text-slate-400">No punches</span>
                      : <div className="flex flex-wrap gap-1">{day.punchTimes.map((t, i) => <span key={i} className="px-1.5 py-0.5 rounded bg-slate-100 font-mono text-xs">{t}</span>)}</div>}
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center">
                    {[["Status", day.status || "-"], ["In", day.arrive || "--"], ["Out", day.depart || "--"], ["OT", day.calculatedOt]].map(([l, v]) => (
                      <div key={l} className="bg-slate-50 rounded-lg py-2">
                        <div className="text-[10px] uppercase text-slate-400 font-semibold">{l}</div>
                        <div className="font-mono font-semibold text-slate-700">{v}</div>
                      </div>
                    ))}
                  </div>
                  {day.workHrs && (
                    <div className="text-xs text-slate-600">
                      Worked (time inside): <b className="font-mono">{day.workHrs}</b>
                      {day.awayHrs && <> · away during the day: <b className="font-mono text-amber-700">{day.awayHrs}</b> (not counted)</>}
                    </div>
                  )}

                  {isAdmin ? (
                    <div className="space-y-3 border-t border-slate-100 pt-4">
                      {day.status === "MP" && (
                        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-2 text-xs">
                          A punch is missing, so this day is not paid yet. Add the missing In or Out time (or choose a status) to settle it.
                        </div>
                      )}
                      <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Correction - In / Out show the machine times, change only what is missing or wrong</div>
                      <label className="block">
                        <span className="text-xs text-slate-500">Status</span>
                        <select value={fixForm.status} onChange={(ev) => setFixForm((f) => ({ ...f, status: ev.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm">
                          {FIX_STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        <label className="block"><span className="text-xs text-slate-500">In</span>{field("arrive", { type: "time" })}</label>
                        <label className="block"><span className="text-xs text-slate-500">Out</span>{field("depart", { type: "time" })}</label>
                        <label className="block"><span className="text-xs text-slate-500">OT (HH:MM)</span>{field("ot", { placeholder: "auto", maxLength: 5 })}</label>
                      </div>
                      <label className="block"><span className="text-xs text-slate-500">Note</span>{field("note", { placeholder: "e.g. forgot to punch out, confirmed by supervisor", maxLength: 255 })}</label>
                      {day.edited && (
                        <div className="text-xs text-slate-400">Last corrected by {day.edited.by || "unknown"}{day.edited.at ? ` on ${day.edited.at}` : ""}</div>
                      )}
                    </div>
                  ) : (
                    day.edited && <div className="text-xs text-indigo-600">Corrected by {day.edited.by || "admin"}{day.edited.note ? `: ${day.edited.note}` : ""}</div>
                  )}
                </div>

                {isAdmin && (
                  <div className="px-5 py-4 border-t border-slate-100 flex items-center gap-2">
                    {day.edited && (
                      <button onClick={() => saveFix(true)} disabled={savingFix} className="px-3 py-2 rounded-lg text-sm font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-40">Remove correction</button>
                    )}
                    <button onClick={() => setEditCell(null)} disabled={savingFix} className="ml-auto px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
                    <button onClick={() => saveFix(false)} disabled={savingFix} className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold disabled:opacity-40">{savingFix ? "Saving..." : "Save"}</button>
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
